import React, { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import api from "../../../../../../services/api";
import { FrontendNotification } from "../../../../../../shared/Notification";
import { ICaixaGeral } from "../../types/types";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";
import type { CaixaMovementType } from "..";

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: ICaixaGeral;
  movementType: CaixaMovementType;
  onConfirm: () => void;
  onClose: () => void;
}

const Form: React.FC<Props> = ({ isEdit, isView, selectedRow, movementType, onConfirm, onClose }: Props) => {
  const [valorMovimentacao, setValorMovimentacao] = useState<number | undefined>();
  const [justificativa, setJustificativa] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const isRetirada = movementType === "retirada";
  const tipoMovimentacao = isRetirada ? "RETIRADA_MANUAL" : "DEPOSITO_MANUAL";

  useEffect(() => {
    if (selectedRow) {
      setValorMovimentacao(Math.abs(Number(selectedRow.valor_movimentacao || 0)));
      setJustificativa(selectedRow.justificativa || "");
    }
  }, [selectedRow]);

  const handleSubmit = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();

    const finalUserId = user?.id || 1;
    const movement = Number(valorMovimentacao || 0);

    if (movement <= 0) {
      FrontendNotification("Informe um valor para a movimentação.", "warning");
      return;
    }

    if (!justificativa.trim()) {
      FrontendNotification("Informe a justificativa da movimentação.", "warning");
      return;
    }

    try {
      setLoading(true);

      if (isEdit && selectedRow?.id_financeiro_caixa_geral) {
        const oldSigned = Number(selectedRow.valor_movimentacao || 0);
        const newSigned = isRetirada ? -Math.abs(movement) : Math.abs(movement);
        await api.post("/editar/financeiroCaixaGeral", {
          id_financeiro_caixa_geral: selectedRow.id_financeiro_caixa_geral,
          total_no_cofre: Number(selectedRow.total_no_cofre || 0) + (newSigned - oldSigned),
          valor_movimentacao: newSigned,
          tipo_movimentacao: tipoMovimentacao,
          justificativa: justificativa.trim(),
          id_usuario_historico: finalUserId,
        });
      } else {
        // O backend define o sinal pelo tipo e recalcula o total no cofre.
        await api.post("/cadastrar/financeiroCaixaGeral", {
          valor_movimentacao: Math.abs(movement),
          tipo_movimentacao: tipoMovimentacao,
          justificativa: justificativa.trim(),
          id_usuario_historico: finalUserId,
          status: 1,
        });
      }

      FrontendNotification("Registro salvo com sucesso!", "success");
      onConfirm();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      FrontendNotification("Erro ao salvar registro", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full p-4">
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor da {isRetirada ? "Retirada" : "Depósito"}
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {isRetirada
                ? "O valor será registrado como saída do caixa."
                : "O valor será registrado como entrada no caixa."}
            </p>
            <NumericFormat
                value={valorMovimentacao ?? ""}
                onValueChange={(values) => setValorMovimentacao(values.floatValue)}
                disabled={isView}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                prefix="R$ "
                placeholder="R$ 0,00"
                className="w-full rounded-full border border-[#F0A800] bg-white px-4 py-3 text-sm text-[#1E2121] outline-none transition focus:border-[#062D4E] focus:ring-2 focus:ring-[#062D4E]/10 disabled:bg-gray-100"
            />
        </div>

        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Justificativa
            </label>
            <textarea
                value={justificativa}
                onChange={(event) => setJustificativa(event.target.value)}
                disabled={isView}
                rows={3}
                maxLength={500}
                placeholder="Descreva o motivo desta movimentação"
                className="w-full resize-none rounded-2xl border border-[#F0A800] bg-white px-4 py-3 text-sm text-[#1E2121] outline-none transition focus:border-[#062D4E] focus:ring-2 focus:ring-[#062D4E]/10 disabled:bg-gray-100"
            />
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 mr-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
                Cancelar
            </button>
            {!isView && (
                <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#062D4E] rounded-md hover:bg-[#05233d]"
                >
                    {loading ? "Salvando..." : "Salvar"}
                </button>
            )}
        </div>
    </form>
  );
};

export default Form;
